import { ReactElement, createContext, useContext, useState } from "react";
import { PreserveContextProps } from "./models/PreserveContextProps";
import { useFeedback } from "./FeedbackContext";
import { toFormData } from "../utils";
import { useLocalization } from "./LocalizationContext";

type PreserveProviderProps = { children: ReactElement[] };

export const PreserveContext = createContext<PreserveContextProps>({
  setInitialState: () => undefined,
  setCurrentState: () => undefined,
  safeExecute: (callback: (shouldGetPlugin?: boolean) => void) => undefined,
  reset: () => undefined
});

export const usePreserve = () => useContext(PreserveContext);

export const PreserveProvider = ({ children }: PreserveProviderProps) => {
  const [initialState, setInitialState] = useState<any>({});
  const [currentState, setCurrentState] = useState<any>({});
  const { showModal } = useFeedback();
  const { translate } = useLocalization();

  const reset = () => {
    setCurrentState({});
    setInitialState({});
  };

  const handlePreserve = (callback: (shouldGetPlugin: boolean) => void) => {
    showModal({
      isVisible: true,
      message: translate("versions.allUnsavedChages"),
      handleClose: () => showModal(null),
      handleContinue: async () => {
        showModal(null);
        reset();
        callback(currentState.resourcesModified);
      }
    });
  };

  const safeExecute = (callback: (shouldGetPlugin?: boolean) => void) => {
    const initialFormData = toFormData(filterResourcesFields(initialState));
    const currentFormData = toFormData(filterResourcesFields(currentState));
    const initialKeysLength = Array.from(initialFormData.keys()).length;
    const currentKeysLength = Array.from(currentFormData.keys()).length;

    if (initialKeysLength !== currentKeysLength) {
      handlePreserve(callback);
      return;
    }

    for (const key of currentFormData.keys()) {
      const current = currentFormData.get(key);
      const initial = initialFormData.get(key);
      const currentNoSpaces = typeof current === "string" ? current.replace(/\s+/g, "") : "";
      const initialNoSpaces = typeof initial === "string" ? initial.replace(/\s+/g, "") : "";

      if (currentNoSpaces !== initialNoSpaces) {
        handlePreserve(callback);
        return;
      }
    }

    reset();
    callback(currentState.resourcesModified);
  };

  const filterResourcesFields = (state: any) => {
    const {
      changelogResources,
      descriptionResources,
      documentationResources,
      resourcesModified,
      htmlChangelog,
      htmlDescription,
      htmlDocumentation,
      ...filteredState
    } = state;
    return filteredState;
  };

  return (
    <PreserveContext.Provider value={{ setCurrentState, setInitialState, safeExecute, reset }}>
      {children}
    </PreserveContext.Provider>
  );
};
